import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { format } from "date-fns";
import { Search, User, Calendar, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { ScrollArea } from "@/components/ui/scroll-area";
import { toast } from "@/hooks/use-toast";

interface Inquiry {
  id: string;
  first_name: string;
  last_name: string;
  email: string;
  wedding_date: string | null;
  venue: string | null;
}

interface LinkInquiryModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  threadId: string;
  onLinked: () => void;
}

export function LinkInquiryModal({ open, onOpenChange, threadId, onLinked }: LinkInquiryModalProps) {
  const [search, setSearch] = useState("");
  const [inquiries, setInquiries] = useState<Inquiry[]>([]);
  const [loading, setLoading] = useState(false);
  const [linking, setLinking] = useState<string | null>(null);

  const fetchInquiries = async (searchTerm: string) => {
    setLoading(true);
    try {
      let query = supabase
        .from("inquiries")
        .select("id, first_name, last_name, email, wedding_date, venue")
        .order("created_at", { ascending: false })
        .limit(20);

      if (searchTerm) {
        query = query.or(`first_name.ilike.%${searchTerm}%,last_name.ilike.%${searchTerm}%,email.ilike.%${searchTerm}%`);
      }

      const { data, error } = await query;
      if (error) throw error;
      setInquiries(data || []);
    } catch (error: any) {
      console.error("Error fetching inquiries:", error);
      toast({
        title: "Error",
        description: "Failed to load inquiries",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open) {
      fetchInquiries("");
    }
  }, [open]);

  useEffect(() => {
    const debounce = setTimeout(() => {
      if (open) {
        fetchInquiries(search);
      }
    }, 300);
    return () => clearTimeout(debounce);
  }, [search, open]);

  const handleLink = async (inquiryId: string) => {
    setLinking(inquiryId);
    try {
      const { error } = await supabase
        .from("email_threads")
        .update({ 
          inquiry_id: inquiryId,
          sender_name: null,
          sender_email: null
        })
        .eq("id", threadId);

      if (error) throw error;

      toast({
        title: "Success",
        description: "Thread linked to inquiry",
      });
      onLinked();
      onOpenChange(false);
    } catch (error: any) {
      console.error("Error linking thread:", error);
      toast({
        title: "Error",
        description: "Failed to link thread to inquiry",
        variant: "destructive",
      });
    } finally {
      setLinking(null);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Link to Inquiry</DialogTitle>
          <DialogDescription>
            Search and select an existing inquiry to link this email thread to.
          </DialogDescription>
        </DialogHeader>

        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search by name or email..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-9"
          />
        </div>

        <ScrollArea className="h-[300px] border rounded-lg">
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            </div>
          ) : inquiries.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <User className="h-8 w-8 mx-auto mb-2 opacity-50" />
              <p>No inquiries found</p>
            </div>
          ) : (
            <div className="p-2 space-y-1">
              {inquiries.map((inquiry) => (
                <button
                  key={inquiry.id}
                  onClick={() => handleLink(inquiry.id)}
                  disabled={linking !== null}
                  className="w-full text-left p-3 rounded-lg hover:bg-muted transition-colors disabled:opacity-50"
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-medium">
                        {inquiry.first_name} {inquiry.last_name}
                      </p>
                      <p className="text-sm text-muted-foreground">{inquiry.email}</p>
                    </div>
                    <div className="text-right">
                      {linking === inquiry.id ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : inquiry.wedding_date ? (
                        <span className="text-xs text-muted-foreground flex items-center gap-1">
                          <Calendar className="h-3 w-3" />
                          {format(new Date(inquiry.wedding_date), "MMM d, yyyy")}
                        </span>
                      ) : null}
                    </div>
                  </div>
                  {inquiry.venue && (
                    <p className="text-xs text-muted-foreground mt-1 truncate">
                      {inquiry.venue}
                    </p>
                  )}
                </button>
              ))}
            </div>
          )}
        </ScrollArea>
      </DialogContent>
    </Dialog>
  );
}